import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  BsCheck2Circle,
  BsBox,
  BsCreditCard,
  BsTruck,
  BsReceipt,
} from "react-icons/bs";

export default function OrderConfirmation() {
  const location = useLocation();
  const orderDetails = location.state?.orderDetails || {
    orderNumber: "AN-2024-1234",
    orderDate: new Date().toLocaleDateString(),
    estimatedDelivery: new Date(
      Date.now() + 7 * 24 * 60 * 60 * 1000
    ).toLocaleDateString(),
    total: "₵350.00",
    subtotal: "₵320.00",
    shipping: "₵30.00",
    paymentMethod: "Credit Card (**** 1234)",
    items: [
      {
        name: "Moisture Retention Bundle",
        quantity: 1,
        price: "₵320.00",
        image: "https://example.com/fallback-image.jpg",
      },
    ],
    shippingAddress: {
      name: "Jane Smith",
      street: "123 Main Street",
      city: "Accra",
      country: "Ghana",
      phone: "+233 24 123 4567",
    },
  };

  return (
    <div className="bg-gray-50 pt-24 pb-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-[#DB5461]/10 rounded-full flex items-center justify-center">
              <BsCheck2Circle className="w-10 h-10 text-[#DB5461]" />
            </div>
          </div>
          <h1 className="text-4xl font-serif font-bold text-gray-900 mb-4">
            Thank You for Your Order!
          </h1>
          <p className="text-lg text-gray-600">
            Order #{orderDetails.orderNumber} has been confirmed
          </p>
        </motion.div>

        {/* Order Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-soft p-8 mb-8"
        >
          <div className="flex items-center gap-4 mb-6">
            <BsTruck className="w-6 h-6 text-[#DB5461]" />
            <div>
              <h2 className="font-medium text-gray-900">Estimated Delivery</h2>
              <p className="text-gray-600">{orderDetails.estimatedDelivery}</p>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <h3 className="font-medium text-gray-900 mb-4">Order Updates</h3>
            <p className="text-gray-600">
              You will receive shipping and delivery updates by email.
            </p>
          </div>
        </motion.div>

        {/* Order Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-soft p-8 mb-8"
        >
          <div className="flex items-center gap-4 mb-6">
            <BsReceipt className="w-6 h-6 text-[#DB5461]" />
            <h2 className="text-xl font-medium text-gray-900">Order Details</h2>
          </div>

          {/* Order Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Order Number
              </h3>
              <p className="text-gray-900">{orderDetails.orderNumber}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Order Date
              </h3>
              <p className="text-gray-900">{orderDetails.orderDate}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Payment Method
              </h3>
              <div className="flex items-center gap-2">
                <BsCreditCard className="text-gray-400" />
                <p className="text-gray-900">{orderDetails.paymentMethod}</p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Shipping Address
              </h3>
              <p className="text-gray-900">
                {orderDetails.shippingAddress.name}
                <br />
                {orderDetails.shippingAddress.street}
                <br />
                {orderDetails.shippingAddress.city},{" "}
                {orderDetails.shippingAddress.country}
              </p>
            </div>
          </div>

          {/* Order Items */}
          <div className="border-t border-gray-100 pt-6">
            <h3 className="font-medium text-gray-900 mb-4">Order Items</h3>
            {orderDetails.items.map((item, index) => (
              <div key={index} className="flex items-center gap-4 mb-4">
                <div className="w-20 h-20 rounded-lg overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{item.name}</h4>
                  <p className="text-gray-600">Quantity: {item.quantity}</p>
                </div>
                <p className="font-medium text-gray-900">{item.price}</p>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="border-t border-gray-100 pt-6 mt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>{orderDetails.subtotal}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span>{orderDetails.shipping}</span>
              </div>
              <div className="flex justify-between text-lg font-medium text-gray-900 pt-2 border-t border-gray-100">
                <span>Total</span>
                <span>{orderDetails.total}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center"
        >
          <Link
            to="/products"
            className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-[#DB5461] text-white hover:bg-[#DB5461]/90 transition-colors duration-300"
          >
            Continue Shopping
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
